<template>
  <div class="w-full max-w-6xl mx-auto px-4 sm:px-6">
    <div class="space-y-4">
      <div class="mt-6 mx-auto max-w-3xl">
        <div class="text-center w-full h-auto">
          <h1
            class="
              text-4xl
              tracking-tight
              font-extrabold
              text-gray-900
              sm:text-5xl
              md:text-6xl
            "
          >
            <span class="block">Now to perfect your</span>
            <span class="block text-mygrad">GraduationClip</span>
          </h1>
          <p
            class="
              mt-2
              max-w-md
              mx-auto
              text-base text-gray-700
              sm:text-lg
              md:mt-5 md:text-xl md:max-w-2xl
            "
          >
            Follow the steps below to check the <b>video</b>, fine tune the
            <b>audio</b>, and then submit your clip.
          </p>
        </div>
      </div>
      <div v-if="showHowToEdit" class="mx-auto w-full max-w-4xl">
        <div
          class="rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm text-slate-700 shadow-sm sm:px-5 sm:py-4"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="font-bold text-slate-900">How to edit your clip</p>
              <div class="mt-2 grid gap-2 md:grid-cols-3">
                <div class="rounded-xl bg-white/70 p-2.5">
                  <p class="font-bold text-slate-900">1. Check the video</p>
                  <p class="mt-1 text-xs leading-5 text-slate-700">
                    Use the two sliders to choose where your clip starts and
                    ends.
                  </p>
                </div>
                <div class="rounded-xl bg-white/70 p-2.5">
                  <p class="font-bold text-slate-900">2. Check the audio</p>
                  <p class="mt-1 text-xs leading-5 text-slate-700">
                    Move the highlighted audio box so it covers the
                    announcement of your name.
                  </p>
                </div>
                <div class="rounded-xl bg-white/70 p-2.5">
                  <p class="font-bold text-slate-900">3. Submit your changes</p>
                  <p class="mt-1 text-xs leading-5 text-slate-700">
                    When everything looks right, submit once to regenerate your
                    clip.
                  </p>
                </div>
              </div>
            </div>
            <button
              type="button"
              @click="dismissHowToEdit"
              class="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-white/80"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
      <div v-if="errors.length" class="mx-auto max-w-3xl">
        <div class="text-center w-full h-auto">
          <p
            class="
              mt-3
              max-w-md
              mx-auto
              text-base text-red-500
              sm:text-lg
              md:mt-5 md:text-xl md:max-w-3xl
            "
          >
            {{ errors[0] }}
          </p>
        </div>
      </div>
      <div class="mx-auto w-full max-w-4xl">
        <div
          class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5"
        >
          <div class="text-center w-full h-auto">
            <p class="text-xs font-bold uppercase tracking-[0.2em] text-mygrad">
              Step 1
            </p>
            <h2 class="text-2xl font-black text-gray-900">Video Timing</h2>
            <p class="mt-2 max-w-2xl mx-auto text-sm text-gray-700">
              Trim the start and end so the clip begins after your first steps
              and ends when you leave the frame.
            </p>
          </div>
          <video-player
            :options="videoOptions"
            ref="videoPlayer"
            @statechanged="playerStateChanged"
            @loadeddata="onPlayerReady"
          ></video-player>
          <div class="mt-4 px-2 sm:px-4">
            <video-slider
              v-if="playerReady"
              :maxThreshold="maxThreshold"
              :minThreshold="minThreshold"
              :min="min"
              :max="max"
              @update:min="changeIn"
              @update:max="changeOut"
            ></video-slider>
            <p class="mt-2 text-center text-xs text-gray-500">
              Left handle = clip start. Right handle = clip end.
            </p>
          </div>
        </div>
      </div>
      <div class="mx-auto w-full max-w-4xl" v-if="audio">
        <div
          class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5"
        >
          <div class="text-center w-full h-auto">
            <p class="text-xs font-bold uppercase tracking-[0.2em] text-mygrad">
              Step 2
            </p>
            <h2 class="text-2xl font-black text-gray-900">Announcement Audio</h2>
            <p class="mt-2 max-w-2xl mx-auto text-sm text-gray-700">
              Move the highlighted box so it covers the full announcement of
              your name, then preview it to check it sounds right.
            </p>
          </div>
          <div id="waveform" class="mt-4"></div>
          <p class="mt-3 text-center text-sm font-medium text-slate-700">
            Drag the middle to move the announcement. Drag either blue grip to
            adjust its start or end.
          </p>
          <div
            v-if="audioState !== 'loading' && showAudioFineTunePrompt"
            class="mt-3 text-center"
          >
            <button
              type="button"
              class="audio-fine-tune-toggle"
              aria-controls="audio-fine-tune-controls"
              :aria-expanded="showAudioFineTuneControls ? 'true' : 'false'"
              @click="showAudioFineTuneControls = !showAudioFineTuneControls"
            >
              {{
                showAudioFineTuneControls
                  ? "Hide precise controls"
                  : "Need precise control?"
              }}
            </button>
          </div>
          <div
            v-if="audioState !== 'loading' && showAudioFineTuneControls"
            id="audio-fine-tune-controls"
            class="audio-adjust-grid mt-3"
            aria-label="Fine tune announcement audio"
          >
            <div class="audio-adjust-control">
              <span class="audio-adjust-label">Start</span>
              <div class="audio-adjust-buttons">
                <button
                  type="button"
                  class="audio-nudge-button"
                  aria-label="Move audio start 0.1 seconds earlier"
                  @click="nudgeAudioBoundary('start', -0.1)"
                >
                  −
                </button>
                <output class="audio-time" aria-live="polite">
                  {{ formatAudioTime(audioMin) }}
                </output>
                <button
                  type="button"
                  class="audio-nudge-button"
                  aria-label="Move audio start 0.1 seconds later"
                  @click="nudgeAudioBoundary('start', 0.1)"
                >
                  +
                </button>
              </div>
            </div>
            <div class="audio-adjust-control">
              <span class="audio-adjust-label">End</span>
              <div class="audio-adjust-buttons">
                <button
                  type="button"
                  class="audio-nudge-button"
                  aria-label="Move audio end 0.1 seconds earlier"
                  @click="nudgeAudioBoundary('end', -0.1)"
                >
                  −
                </button>
                <output class="audio-time" aria-live="polite">
                  {{ formatAudioTime(audioMax) }}
                </output>
                <button
                  type="button"
                  class="audio-nudge-button"
                  aria-label="Move audio end 0.1 seconds later"
                  @click="nudgeAudioBoundary('end', 0.1)"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div class="mt-4 text-center w-full h-auto">
            <button
              v-if="audioState == 'ready'"
              @click="playPreview"
              type="button"
              class="
                inline-flex
                items-center
                px-4
                py-2
                border border-gray-300
                shadow-sm
                text-sm
                font-medium
                rounded-md
                text-gray-700
                bg-white
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-indigo-500
              "
            >
              Preview Audio
            </button>
            <button
              v-else-if="audioState == 'playing'"
              @click="pausePreview"
              type="button"
              class="
                inline-flex
                items-center
                px-4
                py-2
                border border-gray-300
                shadow-sm
                text-sm
                font-medium
                rounded-md
                text-gray-700
                bg-white
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-indigo-500
              "
            >
              Pause
            </button>
          </div>
          <p class="mt-2 text-center text-xs text-gray-500">
            Tip: use Preview Audio to confirm the name announcement starts and
            ends in the right place.
          </p>
        </div>
      </div>
      <div v-else class="mx-auto max-w-4xl">
        <div class="rounded-2xl border border-gray-200 bg-white p-5 text-center shadow-sm">
          <p class="text-xs font-bold uppercase tracking-[0.2em] text-mygrad">
            Step 2
          </p>
          <p class="text-sm text-gray-700">
            Audio preview is not available for this ceremony. You can still
            adjust the video.
          </p>
        </div>
      </div>
      <div class="mx-auto w-full max-w-4xl">
        <div
          class="flex flex-col items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:p-5"
        >
          <div class="text-center sm:text-left">
            <p class="text-xs font-bold uppercase tracking-[0.2em] text-mygrad">
              Step 3
            </p>
            <p class="mt-1 text-sm text-gray-700">
              Submit once to regenerate your updated GraduationClip.
            </p>
          </div>
          <label for="checkbox" class="flex items-center gap-3 text-sm text-gray-700">
            <input type="checkbox" id="checkbox" v-model="include_audio" />
            <span>Include the announcement of my name</span>
          </label>
          <button
            @click="submit"
            type="submit"
            class="
              inline-flex
              items-center
              px-5
              py-2.5
              border border-transparent
              shadow-sm
              text-sm
              font-semibold
              rounded-md
              text-white
              bg-mygrad
              hover:opacity-90
              focus:outline-none
              focus:ring-2
              focus:ring-offset-2
              focus:ring-indigo-500
              disabled:opacity-50!important
            "
            :disabled="!playerReady"
          >
            Submit Clip Changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "../../plugins/video-player";
import WaveSurfer from "wavesurfer.js";
import Regions from "wavesurfer.js/dist/plugins/regions.js";
import Minimap from "wavesurfer.js/dist/plugins/minimap.js";
import VideoSlider from "../VideoSlider.vue";

function parseOptionalNumber(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default {
  name: "ManualSubmissionPage",
  props: {
    focalPoint: Number,
    ceremony: Object,
    errors: Array,
    audio: String,
    peaks: Object,
    initialIn: [Number, String],
    initialOut: [Number, String],
    initialAudioIn: [Number, String],
    initialAudioOut: [Number, String],
  },
  data: function () {
    const initialIn = parseOptionalNumber(this.initialIn);
    const initialOut = parseOptionalNumber(this.initialOut);
    const initialAudioIn = parseOptionalNumber(this.initialAudioIn);
    const initialAudioOut = parseOptionalNumber(this.initialAudioOut);
    return {
      videoOptions: {
        autoplay: false,
        preload: "auto",
        playToggle: false,
        controlBar: {
          playToggle: false,
          currentTimeDisplay: false,
          durationDisplay: false,
          timeControlDisplay: false,
          remainingTimeDisplay: false,
          progressControl: {
            seekBar: false,
          },
          fullscreenToggle: false,
          muteToggle: false,
          volumeBar: false,
          playbackRateMenuButton: false,
        },
        sources: [
          {
            type: "application/x-mpegURL",
            src: this.ceremony.video,
          },
        ],
        aspectRation: "16:9",
        responsive: true,
      },
      min: initialIn !== null ? initialIn : this.focalPoint - 4,
      max: initialOut !== null ? initialOut : this.focalPoint + 10,
      audioMin:
        initialAudioIn !== null ? initialAudioIn : Math.max(this.focalPoint - 2, 0),
      audioMax:
        initialAudioOut !== null ? initialAudioOut : this.focalPoint + 1,
      include_audio: true,
      playerReady: false,
      surfer: null,
      regionsPlugin: null,
      minimapPlugin: null,
      audioState: "loading",
      showHowToEdit: true,
      showAudioFineTunePrompt: false,
      showAudioFineTuneControls: false,
    };
  },
  computed: {
    player() {
      return this.$refs.videoPlayer.player;
    },
    minThreshold() {
      return Math.min(this.focalPoint - 4, this.min);
    },
    maxThreshold() {
      return Math.max(this.focalPoint + 10, this.max);
    },
  },
  mounted() {
    this.showHowToEdit =
      window.localStorage.getItem("graduationclip-combined-editor-help-dismissed") !==
      "true";
    if (this.audio) {
      this.initWaveSurfer();
    } else {
      this.audioState = "unavailable";
    }
  },
  beforeDestroy() {
    if (this.surfer) {
      this.surfer.destroy();
      this.surfer = null;
    }
  },
  methods: {
    changeIn(value) {
      this.min = parseFloat(value);
      this.player.currentTime(value);
      this.player.pause();
    },
    changeOut(value) {
      this.max = parseFloat(value);
      this.player.currentTime(value);
      this.player.pause();
    },
    onPlayerReady(player) {
      this.playerReady = true;
      this.player.pause();
      player.currentTime(this.min);
    },
    onPlayerPlay(player) {
      console.log("player play!", player);
    },
    onPlayerPause(player) {
      console.log("player pause!", player);
      console.log("Current time: ", player.currentTime());
    },
    playerStateChanged(event) {
      if ("loadeddata" in event && event.loadeddata) {
        this.onPlayerReady(this.player);
      }
      if ("pause" in event && event.pause) {
        this.onPlayerPause(this.player);
      } else if ("play" in event && event.play) {
        this.onPlayerPlay(this.player);
      }
    },
    initWaveSurfer() {
      this.regionsPlugin = Regions.create();
      this.minimapPlugin = Minimap.create({
        height: 20,
        waveColor: "#ddd",
        progressColor: "#999",
      });

      this.surfer = WaveSurfer.create({
        container: "#waveform",
        height: 140,
        waveColor: "rgb(207,217,237)",
        progressColor: "#60A5FA",
        backend: "MediaElement",
        hideScrollbar: true,
        normalize: true,
        autoplay: false,
        interact: false,
        minPxPerSec: 25,
        autoScroll: true,
        autoCenter: true,
        fillParent: true,
        url: this.audio,
        peaks: this.peaks?.data || undefined,
        plugins: [this.regionsPlugin, this.minimapPlugin],
      });

      this.surfer.on("ready", () => {
        this.audioState = "ready";
        const selectionMidpoint = (this.audioMin + this.audioMax) / 2;
        this.regionsPlugin.addRegion({
          start: this.audioMin,
          end: this.audioMax,
          content: "My Name",
          drag: true,
          resize: true,
          maxLength: 17,
          minLength: 1,
        });
        this.surfer.once("redrawcomplete", () => {
          this.surfer.setTime(selectionMidpoint);
        });
      });

      this.regionsPlugin.on("region-updated", (region) => {
        this.updateAudioSelection(region);
        this.offerAudioFineTuneControls();
        this.surfer.play(region.start, region.end);
      });

      this.regionsPlugin.on("region-update", (region) => {
        this.updateAudioSelection(region);
      });

      this.regionsPlugin.on("region-clicked", (region, e) => {
        e.stopPropagation();
        this.offerAudioFineTuneControls();
        this.surfer.play(region.start, region.end);
      });

      this.surfer.on("play", () => {
        this.audioState = "playing";
      });

      this.surfer.on("pause", () => {
        this.audioState = "ready";
      });
    },
    playPreview() {
      if (!this.regionsPlugin) {
        return;
      }
      const region = this.regionsPlugin.getRegions()[0];
      if (region) {
        this.surfer.play(region.start, region.end);
      }
    },
    pausePreview() {
      if (this.surfer) {
        this.surfer.pause();
      }
    },
    updateAudioSelection(region) {
      this.audioMin = region.start;
      this.audioMax = region.end;
    },
    offerAudioFineTuneControls() {
      this.showAudioFineTunePrompt = true;
    },
    nudgeAudioBoundary(boundary, delta) {
      if (!this.regionsPlugin || !this.surfer) {
        return;
      }
      const region = this.regionsPlugin.getRegions()[0];
      if (!region) {
        return;
      }

      const minimumLength = 1;
      const maximumLength = 17;
      const duration = this.surfer.getDuration();
      let start = region.start;
      let end = region.end;

      if (boundary === "start") {
        const earliestStart = Math.max(0, end - maximumLength);
        const latestStart = end - minimumLength;
        start = Math.min(latestStart, Math.max(earliestStart, start + delta));
      } else {
        const earliestEnd = start + minimumLength;
        const latestEnd = Math.min(duration, start + maximumLength);
        end = Math.max(earliestEnd, Math.min(latestEnd, end + delta));
      }

      region.setOptions({ start, end });
      this.updateAudioSelection(region);
    },
    formatAudioTime(value) {
      const time = Math.max(0, Number(value) || 0);
      const minutes = Math.floor(time / 60);
      const seconds = (time % 60).toFixed(1).padStart(4, "0");
      return `${minutes}:${seconds}`;
    },
    dismissHowToEdit() {
      this.showHowToEdit = false;
      window.localStorage.setItem(
        "graduationclip-combined-editor-help-dismissed",
        "true"
      );
    },
    submit() {
      this.$emit("submit", {
        min: this.min,
        max: this.max,
        include_audio: this.include_audio,
        audio_in: this.include_audio ? this.audioMin : null,
        audio_out: this.include_audio ? this.audioMax : null,
      });
    },
  },
  components: {
    VideoSlider,
  },
};
</script>

<style>
video {
  min-width: 100%;
  position: relative !important;
}

.video-js .vjs-time-divider {
  display: none !important;
}

#waveform {
  width: 100% !important;
  min-height: 140px;
}

#waveform ::part(region) {
  background-color: rgba(14, 165, 233, 0.2) !important;
  border: 2px solid #0369a1;
  touch-action: pan-y;
}

#waveform ::part(region-handle) {
  width: 44px !important;
  border: 0 !important;
  background: linear-gradient(
    to right,
    transparent 19px,
    #0369a1 19px,
    #0369a1 25px,
    transparent 25px
  );
  touch-action: none;
}

#waveform ::part(region-handle-left) {
  left: -22px !important;
}

#waveform ::part(region-handle-right) {
  right: -22px !important;
}

.audio-adjust-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
}

.audio-fine-tune-toggle {
  min-height: 44px;
  border: 0;
  border-radius: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: transparent;
  color: #075985;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.2em;
}

.audio-fine-tune-toggle:hover {
  background: #f0f9ff;
}

.audio-fine-tune-toggle:focus-visible {
  outline: 3px solid rgba(14, 165, 233, 0.45);
  outline-offset: 2px;
}

.audio-adjust-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
}

.audio-adjust-label {
  color: #334155;
  font-size: 0.875rem;
  font-weight: 700;
}

.audio-adjust-buttons {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.audio-nudge-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #fff;
  color: #075985;
  font-size: 1.25rem;
  font-weight: 700;
}

.audio-nudge-button:focus-visible {
  outline: 3px solid rgba(14, 165, 233, 0.45);
  outline-offset: 2px;
}

.audio-time {
  min-width: 4.75rem;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: center;
}

@media (min-width: 640px) {
  .audio-adjust-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.video-js {
  width: 100% !important;
  height: auto !important;
}

.video-js video {
  width: 100% !important;
  height: auto !important;
}
</style>
